import * as React from "react";
import { useMediaQuery } from "@material-ui/core";
import MediaQuery from "react-responsive";

import { ScreenType } from "contexts";
import { appendToClassName } from "lib/helpers";
import theme from "theme";

export const breakpoints = {
  mobile: 768,
  desktop: 960,
  smallDesktop: 1080,
  bigDesktop: 1400,
};

export function Desktop(props: { children?: React.ReactNode }) {
  return (
    <MediaQuery minWidth={breakpoints.desktop}>{props.children}</MediaQuery>
  );
}

export function Mobile(props: { children?: React.ReactNode }) {
  return (
    <MediaQuery maxWidth={breakpoints.desktop - 1}>{props.children}</MediaQuery>
  );
}

export function SmallMobile(props: { children?: React.ReactNode }) {
  return (
    <MediaQuery maxWidth={breakpoints.mobile}>{props.children}</MediaQuery>
  );
}

export function DesktopDiv(props: {
  children?: React.ReactNode;
  className?: string;
}) {
  const { children, className } = props;

  return (
    <Desktop>
      <div className={`desktop ${className}`}>{children}</div>
    </Desktop>
  );
}

export function MobileDiv(props: {
  children?: React.ReactNode;
  className?: string;
}) {
  const { children, className } = props;

  return (
    <Mobile>
      <div className={`mobile ${className}`}>{children}</div>
    </Mobile>
  );
}

export interface ResponsiveButtonProps {
  id: string;
  className?: { [screeType in ScreenType]: string };
  style?: { [screeType in ScreenType]: React.CSSProperties };
  children?: React.ReactNode;
  onClick?: () => void;
}

export class ResponsiveButton extends React.Component<ResponsiveButtonProps> {
  public render() {
    const { id, className, style, onClick, children } = this.props;

    return (
      <>
        <Desktop>
          <button
            id={id}
            className={className.desktop}
            style={style.desktop}
            onClick={onClick}
          >
            {children}
          </button>
        </Desktop>
        <Mobile>
          <button
            className={className.mobile}
            style={style.mobile}
            onClick={onClick}
          >
            {children}
          </button>
        </Mobile>
      </>
    );
  }
}

export interface DivProperties {
  mobile?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  desktop?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  commonStyle?: React.CSSProperties;
  children?: React.ReactNode;
  id?: string;
  className?: string;
  onClick?: () => void;
  externalRef?: React.RefObject<HTMLDivElement>;
}

export const Div: React.FC<DivProperties> = ({
  desktop,
  mobile,
  commonStyle,
  id,
  children,
  className,
  externalRef,
  onClick,
}) => {
  desktop = desktop || {};
  mobile = mobile || {};
  commonStyle = commonStyle || {};

  mobile.style = {
    ...commonStyle,
    ...mobile.style,
  };
  desktop.style = {
    ...commonStyle,
    ...desktop.style,
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <>
      {isMobile ? (
        <div
          id={id || ""}
          className={appendToClassName(
            className,
            appendToClassName("mobile", mobile.className),
          )}
          {...mobile}
          ref={externalRef}
          onClick={onClick}
        >
          {children}
        </div>
      ) : (
        <div
          id={id || ""}
          className={appendToClassName(
            className,
            appendToClassName("desktop", desktop.className),
          )}
          {...desktop}
          ref={externalRef}
          onClick={onClick}
        >
          {children}
        </div>
      )}
    </>
  );
};

export interface TDProperties {
  mobile?: React.DetailedHTMLProps<
    React.TdHTMLAttributes<HTMLTableDataCellElement>,
    HTMLTableDataCellElement
  >;
  desktop?: React.DetailedHTMLProps<
    React.TdHTMLAttributes<HTMLTableDataCellElement>,
    HTMLTableDataCellElement
  >;
  commonStyle?: React.CSSProperties;
  children?: React.ReactNode;
}

export function TD(props: TDProperties) {
  let { desktop, mobile, commonStyle } = props;

  const { children } = props;

  desktop = desktop || {};
  mobile = mobile || {};
  commonStyle = commonStyle || {};

  mobile.style = {
    ...commonStyle,
    ...mobile.style,
  };
  desktop.style = {
    ...commonStyle,
    ...desktop.style,
  };

  return (
    <>
      <Desktop>
        <td {...desktop}>{children}</td>
      </Desktop>
      <Mobile>
        <td {...mobile}>{children}</td>
      </Mobile>
    </>
  );
}
interface ResponsiveDivProps {
  children?: React.ReactNode;
  responsiveStyle?: { [screenSize: number]: React.CSSProperties };
  className?: string;
}

export function ResponsiveDiv(props: ResponsiveDivProps) {
  const { responsiveStyle, children, className } = props;

  const breakPoints = Object.keys(responsiveStyle)
    .map(x => parseInt(x, 10))
    .sort();
  const breakPointCount = breakPoints.length;

  const firstBreakPoint = breakPoints[0];
  const firstBreakPointStyle = responsiveStyle[firstBreakPoint];

  const lastBreakPoint = breakPoints[Math.max(0, breakPointCount - 2)] + 1;
  const lastBreakPointStyle = responsiveStyle[breakPoints[breakPointCount - 1]];

  return (
    <>
      <MediaQuery maxWidth={firstBreakPoint}>
        <div
          className={`${className} screen-size-${firstBreakPoint}-lower`}
          style={firstBreakPointStyle}
        >
          {children}
        </div>
      </MediaQuery>
      {breakPoints.slice(1, breakPointCount - 1).map((breakPoint, i) => {
        return (
          <MediaQuery
            key={breakPoint}
            minWidth={breakPoints[i] + 1}
            maxWidth={breakPoint}
          >
            <div
              className={`${className} screen-size-${breakPoint}`}
              style={responsiveStyle[breakPoint]}
            >
              {children}
            </div>
          </MediaQuery>
        );
      })}
      <MediaQuery minWidth={lastBreakPoint}>
        <div
          className={`${className} screen-size-${lastBreakPoint}-on`}
          style={lastBreakPointStyle}
        >
          {children}
        </div>
      </MediaQuery>
    </>
  );
}

export interface H1Props {
  onDesktop?: string;
  onMobile?: string;
}

export function H1(props: H1Props) {
  const { onDesktop, onMobile } = props;

  return (
    <>
      <h1 className="responsive-h1-desktop">{onDesktop}</h1>
      <h1 className="responsive-h1-mobile">{onMobile}</h1>
    </>
  );
}
